import { createFileRoute, useSearch } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

type State =
  | { kind: 'loading' }
  | { kind: 'valid' }
  | { kind: 'already' }
  | { kind: 'invalid' }
  | { kind: 'success' }
  | { kind: 'error'; message: string }

export const Route = createFileRoute('/unsubscribe')({
  validateSearch: (s: Record<string, unknown>) => ({
    token: typeof s.token === 'string' ? s.token : '',
  }),
  component: UnsubscribePage,
  head: () => ({ meta: [{ title: 'Unsubscribe · Mindful Bridge' }] }),
})

function UnsubscribePage() {
  const { token } = useSearch({ from: '/unsubscribe' })
  const [state, setState] = useState<State>({ kind: 'loading' })
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!token) {
      setState({ kind: 'invalid' })
      return
    }
    ;(async () => {
      try {
        const res = await fetch(
          `/email/unsubscribe?token=${encodeURIComponent(token)}`,
        )
        const data = await res.json()
        if (!res.ok) {
          setState({ kind: 'invalid' })
        } else if (data.valid === false && data.reason === 'already_unsubscribed') {
          setState({ kind: 'already' })
        } else if (data.valid) {
          setState({ kind: 'valid' })
        } else {
          setState({ kind: 'invalid' })
        }
      } catch (e) {
        setState({
          kind: 'error',
          message: e instanceof Error ? e.message : 'Network error',
        })
      }
    })()
  }, [token])

  const confirm = async () => {
    setSubmitting(true)
    try {
      const res = await fetch('/email/unsubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      const data = await res.json()
      if (data.success) setState({ kind: 'success' })
      else if (data.reason === 'already_unsubscribed') setState({ kind: 'already' })
      else
        setState({
          kind: 'error',
          message: data.error ?? 'Could not unsubscribe',
        })
    } catch (e) {
      setState({
        kind: 'error',
        message: e instanceof Error ? e.message : 'Network error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="glass rounded-3xl max-w-md w-full p-8 text-center space-y-4">
        <h1 className="text-2xl font-medium">Email preferences</h1>
        {state.kind === 'loading' && (
          <p className="text-sm text-muted-foreground">Checking your link…</p>
        )}
        {state.kind === 'valid' && (
          <>
            <p className="text-sm text-muted-foreground">
              Click below to unsubscribe from emails from Mindful Bridge.
            </p>
            <button
              onClick={confirm}
              disabled={submitting}
              className="px-5 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {submitting ? 'Unsubscribing…' : 'Confirm unsubscribe'}
            </button>
          </>
        )}
        {state.kind === 'success' && (
          <p className="text-sm text-emerald-400">
            You've been unsubscribed. You won't receive any more emails from us.
          </p>
        )}
        {state.kind === 'already' && (
          <p className="text-sm text-muted-foreground">
            This address is already unsubscribed.
          </p>
        )}
        {state.kind === 'invalid' && (
          <p className="text-sm text-amber-400">
            This unsubscribe link is invalid or has expired.
          </p>
        )}
        {state.kind === 'error' && (
          <p className="text-sm text-rose-400">{state.message}</p>
        )}
      </div>
    </div>
  )
}
